<template>
  <v-dialog
    v-model="bugReportDialog"
    width="480">
    <slot
      slot="activator"
      name="activator">
      <v-btn
        flat
        icon
        color="secondary"
        class="activator-button">
        <v-icon
          class="activator-button__icon">
          bug_report
        </v-icon>
      </v-btn>
    </slot>

    <v-form
      v-if="!loading && !submitSuccess && !submitError"
      ref="bugReport"
      v-model="valid"
      class="bug-report">
      <h2 class="bug-report__header">Report a Bug</h2>
      <p class="bug-report__instructions">
        Please provide a detailed description of the issue, including:
        <ol>
          <li>What you were doing when the problem occurred</li>
          <li>What you expected to happen</li>
          <li>What actually happened</li>
        </ol>
      </p>
      <v-textarea
        ref="bugDescription"
        v-model="formData.description"
        label="Bug Description"
        :rules="[
          v => !!v || 'This field is required',
        ]"
        required />
      <div class="bug-report__screenshot-row">
        <div class="bug-report__subheader">include screenshot: (optional)</div>
        <v-progress-linear
          v-if="loadingImage"
          indeterminate />
        <v-img
          v-if="uploadedImage.base64"
          :src="uploadedImage.base64" />
        <input
          type="file"
          class="bug-report__image-upload"
          @change="setImage">
      </div>
      <div class="bug-report__action-row">
        <v-btn
          flat
          color="primary"
          class="bug-report__close-button"
          @click="bugReportDialog = false">
          close
        </v-btn>
        <v-btn
          outline
          color="primary"
          class="bug-report__submit-button"
          :disabled="!valid"
          @click="submitBugReportForm">
          submit
        </v-btn>
      </div>
    </v-form>

    <v-card v-if="loading">
      <v-card-text class="submitting">
        <h3 class="submitting__header">submitting bug report</h3>
        <v-progress-circular
          indeterminate
          size="100"
          width="7"
          value="true"
          class="submitting__loader" />
      </v-card-text>
    </v-card>

    <v-card
      v-if="submitSuccess"
      color="success"
      dark>
      <v-card-text class="submitting">
        <h3 class="submitting__header">Bug Reported</h3>
        <v-icon class="submitting__icon">done</v-icon>
        <p>
          Thank you for taking the time to report a bug; we'll take a look at it and try to fix it as soon as we can.
        </p>
      </v-card-text>
    </v-card>

    <v-card
      v-if="submitError"
      color="error"
      dark>
      <v-card-text class="submitting">
        <h3 class="submitting__header">Error</h3>
        <v-icon class="submitting__icon">error</v-icon>
        <p>
          Ironically, there seems to be a bug here; please try again later.
        </p>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import api from '../utils/api';

export default {
  name: 'ReportBug',
  data() {
    return {
      loading: false,
      loadingImage: false,
      submitSuccess: false,
      submitError: false,
      bugReportDialog: false,
      valid: false,
      formData: {
        description: '',
        currentRoute: '',
        vuexState: '',
      },
      uploadedImage: {
        base64: '',
        filename: '',
      },
    };
  },
  created() {
    this.initForm();
  },
  methods: {
    initForm() {
      this.clearForm();
      this.setMetaData();
    },
    clearForm() {
      this.loading = true;
      this.formData = {
        description: '',
        currentRoute: '',
        vuexState: '',
      };
      this.uploadedImage = {
        base64: '',
        filename: '',
      };
      this.loading = false;
      this.loadingImage = false;
      this.submitSuccess = false;
      this.submitError = false;
    },
    setMetaData() {
      this.formData.currentRoute = window.location.href;
      this.formData.vuexState = this.$store.state;
    },
    createBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = error => reject(error);
      });
    },
    setImage(event) {
      this.loadingImage = true;
      const imageFile = event.target.files[0] || event.dataTransfer.files[0];
      const image = {
        filename: '',
        base64: '',
      };
      image.filename = imageFile.name;
      this.createBase64(imageFile)
        .then((url) => {
          image.base64 = url;
          this.loadingImage = false;
        })
        .catch(() => {
          this.loadingImage = false;
        });
      this.uploadedImage = image;
    },

    submitBugReportForm() {
      this.loading = true;
      const data = {
        uploadedImage: this.uploadedImage,
        formData: this.formData,
      };
      if (this.$refs.bugReport.validate()) {
        api
          .postData('/storefront/bug_reports/', data)
          .then((res) => {
            if (res.data.messsage !== 'error writing bug report to file') {
              this.loading = false;
              this.submitSuccess = true;
              setTimeout(() => {
                this.bugReportDialog = false;
                this.initForm();
              }, 4000);
            } else {
              this.loading = false;
              this.submitError = true;
              setTimeout(() => {
                this.bugReportDialog = false;
                this.initForm();
              }, 4000);
            }
          })
          .catch(() => {
            this.loading = false;
            this.submitError = true;
            setTimeout(() => {
              this.bugReportDialog = false;
              this.initForm();
            }, 4000);
          });
      }
    },
  },
};
</script>

<style lang="stylus" scoped>
@require('../stylus/base')

.activator-button
  margin: 0

.bug-report
  background-color: $white
  padding: $base-padding*2

  &__header
    @media screen and (max-width: 48rem)
      font-size: 1.5rem

  &__instructions
    @media screen and (max-width: 48rem)
      font-size: $font-size--small

  &__subheader
    @extends $base-subheader

  &__screenshot-row
    padding: $base-padding 0

  &__image-upload
    width: 100%;
    margin-top: $base-padding
    padding: $base-padding
    border: 1px solid $accent
    border-radius: $base-border-radius

  &__action-row
    display: flex
    flex-flow: row nowrap
    align-items: center
    justify-content: flex-end
  &__close-button
    margin: 0
    margin-right: $base-padding
  &__submit-button
    margin: 0

.submitting
  display: flex
  flex-flow: column nowrap
  align-items: center
  justify-content: center
  padding: $base-padding*2
  text-align: center
  border-radius: $base-border-radius
  transition: $base-transition
  &__header
    font-size: 1.5rem
    font-weight: $base-header-font-weight
    text-align: center
  &__loader
    margin: $base-padding*4
  &__icon
    color: currentColor
    font-size: 8rem

</style>
